import { z } from 'zod';
import { type Locale, trSchema } from './locales';

/**
 * Home promo tile (client request 006): one admin-written promotion or notice shown beside the
 * two featured cars. Stored in `settings` under `home_promo`.
 */
export const HOME_PROMO_KEY = 'home_promo';

/**
 * Button link: empty (no button), a path on this site (`/frota`), or an `https://` URL.
 * Rejects `//host`, backslashes, whitespace and control characters, so a value can't turn a
 * site path into an off-site one or smuggle a `javascript:` scheme.
 */
export function isSafePromoHref(href: string): boolean {
  if (href === '') return true;
  if (/[\s\\]/.test(href)) return false;
  if ([...href].some((ch) => ch.charCodeAt(0) < 0x20 || ch.charCodeAt(0) === 0x7f)) return false;
  if (href.startsWith('/')) return !href.startsWith('//');
  try {
    return new URL(href).protocol === 'https:';
  } catch {
    return false;
  }
}

/** Promo picture — written by the admin upload (`processImage`, prefix `promo`). */
export const promoImageSchema = z.object({
  key: z.string().regex(/^promo\/[0-9a-f-]{36}$/),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  blurDataUrl: z.string().startsWith('data:image/').max(20_000),
});
export type PromoImage = z.infer<typeof promoImageSchema>;

export const homePromoSettingSchema = z
  .object({
    enabled: z.boolean(),
    title: trSchema,
    body: trSchema,
    ctaLabel: trSchema,
    ctaHref: z.string().max(500).refine(isSafePromoHref, 'ctaHref must be /path or https://…'),
    // Optional: settings saved before pictures existed have no `image`.
    image: promoImageSchema.nullable().optional(),
  })
  .refine((p) => !p.enabled || p.title.pt.trim().length > 0, 'title.pt required when enabled')
  .refine((p) => p.ctaHref === '' || p.ctaLabel.pt.trim().length > 0, 'ctaLabel.pt required with a link');

export type HomePromoSetting = z.infer<typeof homePromoSettingSchema>;

/** What the public site renders — one locale, PT where a translation is blank. */
export interface HomePromo {
  title: string;
  body: string;
  ctaLabel: string;
  ctaHref: string;
  image: PromoImage | null;
}

export function resolveHomePromo(value: unknown, locale: Locale): HomePromo | null {
  const parsed = homePromoSettingSchema.safeParse(value);
  if (!parsed.success || !parsed.data.enabled) return null;
  const p = parsed.data;
  const pick = (tr: HomePromoSetting['title']) => tr[locale].trim() || tr.pt.trim();
  return {
    title: pick(p.title),
    body: pick(p.body),
    ctaLabel: p.ctaHref ? pick(p.ctaLabel) : '',
    ctaHref: p.ctaHref,
    image: p.image ?? null,
  };
}
